import { fromUnixTime } from 'date-fns';
import { getUnixTime } from 'date-fns/fp/getUnixTime';
import { normalizePlatform } from 'src/models/platform/normalizers';
import { Timestamp } from 'src/types/utilityTypes';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { decamelizeKeys } from 'src/utils/decamelize-keys';

import {
  centsToDollars,
  dateToTimestamp,
  dollarsToCents,
  unNullifyArrays,
  unstringifyBooleans,
} from '../../utils/fieldNormalizers';

import {
  AccountManager,
  ClientSearchResult,
  NotificationSetting,
  RepMax,
  StartCCForClient,
  User,
} from './_types';

const booleanKeys: string[] = ['useDarkMode'];
const arrayKeys: string[] = ['trainingInfoFields'];

export function normalizeUser(user: any): User {
  let camelized = camelcaseKeys(user, { deep: true });

  camelized = unstringifyBooleans(camelized, booleanKeys);
  if (camelized.birthdate) {
    camelized.birthdate = convertUTCDate(camelized.birthdate);
  }

  // Handle platform relationship
  if (user?.platform) {
    camelized.platform = normalizePlatform(user.platform);
  }

  return {
    ...unNullifyArrays(camelized, arrayKeys),
    serviceBalanceCache: centsToDollars(camelized.serviceBalanceCache),
    balanceCache: centsToDollars(camelized.balanceCache),
  } as User;
}

export function denormalizeUser(user: Partial<User>): any {
  const { defaultClientFilters, ...rest } = user;
  const decamelized = decamelizeKeys(rest, { deep: true });

  // Convert rep_maxes created field to timestamp
  if (decamelized.rep_maxes) {
    decamelized.rep_maxes = decamelized.rep_maxes.map((repMax: RepMax) => ({
      ...repMax,
      created:
        repMax.created instanceof Date
          ? dateToTimestamp(repMax.created) || new Date()
          : repMax.created,
    }));
  }

  return {
    ...decamelized,
    default_client_filters: defaultClientFilters,
    ...(decamelized.service_balance_cache && {
      service_balance_cache: dollarsToCents(decamelized.service_balance_cache),
    }),
    ...(decamelized.balance_cache && {
      balance_cache: dollarsToCents(decamelized.balance_cache),
    }),
    ...(decamelized.birthdate && {
      birthdate: convertDateToUTC(decamelized.birthdate),
    }),
  };
}

export function normalizeClientSearchResult(result: any): ClientSearchResult {
  const { json } = result.data;
  const camelized = camelcaseKeys(json);
  const searchResult: ClientSearchResult = {
    id: camelized.userId,
    clientId: camelized.id,
    name: result.term,
    calendarId: camelized.calendarId,
    profileImage: camelized.image,
    email: result.aliases[0] || '',
    aliases: result.aliases,
    phoneNumber: result.phone_number,
    kioskPinRegistered: result.kiosk_pin_registered,
  };

  return searchResult;
}

export function normalizeAccountManager(accountManager: any): AccountManager {
  let camelized = camelcaseKeys(accountManager);
  camelized = unstringifyBooleans(camelized, booleanKeys);
  return unNullifyArrays(camelized, arrayKeys) as AccountManager;
}

// converts timestamps that are UTC but need to be displayed in local time.
// For instance birthdate is returned as midnight UTC of the birthdate
function convertUTCDate(prop: number): Date {
  const date = fromUnixTime(prop);

  // For birthdate, we need to create a date that shows the correct day in local time
  // Extract the UTC components and create a local date with those components
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();

  // Create a new date in local time with the same year/month/day
  const localDate = new Date(year, month, day, 0, 0, 0, 0);

  return localDate;
}

// Converts a local Date to midnight UTC on the same calendar date
// Example: April 10th EDT becomes April 10th 00:00:00 UTC
function convertDateToUTC(prop: Date): Timestamp {
  // Create date with same year/month/day but at UTC midnight
  const utcDate = new Date(
    Date.UTC(prop.getFullYear(), prop.getMonth(), prop.getDate(), 0, 0, 0, 0)
  );
  return getUnixTime(utcDate);
}

export function normalizeClient(client: any): User {
  let camelized = camelcaseKeys(client);
  camelized = unstringifyBooleans(camelized, booleanKeys);
  camelized.birthdate = convertUTCDate(camelized.birthdate);
  return unNullifyArrays(camelized, arrayKeys) as User;
}

export function normalizeNotificationSetting(
  setting: any
): NotificationSetting {
  return camelcaseKeys(setting) as NotificationSetting;
}

export const denormalizeNotificationSetting = (
  setting: NotificationSetting
) => {
  return decamelizeKeys(setting, { deep: true });
};

export const normalizeStartCCForClient = (data: any): StartCCForClient => {
  return camelcaseKeys(data, { deep: true });
};
